import type { DefaultStatusValueModel } from './competitor-engagement-statuses';

/**
 * PD-17606 — what a call outcome or an email reply MEANS, whatever the tenant
 * calls it (campaigns v2, layer 1 of the four-layer outcome model:
 * docs/claude/campaigns/01-decisions.md "Outcomes and configuration").
 *
 * The names are tenant-editable catalog values (Settings → Catalog Values);
 * each value maps to one of these fixed meanings, the way a Competitor Status
 * maps to its outcome. The campaign engine and the account stage rules read
 * the meaning, never the name, so a rename changes nothing they do.
 */

/**
 * A logged call, by meaning:
 * - conversation: spoke with the person (ends the campaign run);
 * - attempt: rang, voicemail or a message with someone else (completes a
 *   call step; the next step follows);
 * - call_back: asked to be called at another time;
 * - not_interested: said no (moves the account to Not Interested);
 * - do_not_call: asked not to be called again;
 * - bad_number: the number is wrong or dead (the queue's "Needs fixing").
 */
export const CALL_OUTCOME_MEANINGS = [
	'conversation',
	'attempt',
	'call_back',
	'not_interested',
	'do_not_call',
	'bad_number',
] as const;
export type CallOutcomeMeaning = (typeof CALL_OUTCOME_MEANINGS)[number];

export const CALL_OUTCOME_MEANING_LABELS: Readonly<Record<CallOutcomeMeaning, string>> = {
	conversation: 'Conversation',
	attempt: 'Attempt',
	call_back: 'Call Back',
	not_interested: 'Not Interested',
	do_not_call: 'Do Not Call',
	bad_number: 'Bad Number',
};

/**
 * The meaning of each default call outcome seeded by PD-14436
 * (SeedSoftphoneCallDispositions). Every production tenant carried exactly
 * these seven on 10/26/26; matched by name, ignoring case.
 */
export const DEFAULT_CALL_DISPOSITION_MEANINGS: ReadonlyArray<DefaultStatusValueModel> = [
	{ name: 'Spoke to customer', outcome: 'conversation' },
	{ name: 'Left voicemail', outcome: 'attempt' },
	{ name: 'Left message with someone', outcome: 'attempt' },
	{ name: 'No answer / hung up', outcome: 'attempt' },
	{ name: 'Wrong number', outcome: 'bad_number' },
	{ name: 'Not interested', outcome: 'not_interested' },
	{ name: 'Follow-up scheduled', outcome: 'call_back' },
];

/**
 * An email reply, by meaning (Workd's own categories; Instantly's statuses
 * are mapped onto them on the Instantly settings page):
 * - interested: wants to hear more;
 * - meeting: wants to meet or asked for a time;
 * - question: asked something that needs an answer;
 * - referral: pointed us at someone else;
 * - later: not now, try again later;
 * - not_interested: said no;
 * - out_of_office: an automatic away reply (pauses the run, never ends it);
 * - unsubscribe: asked to stop (no more email, ever);
 * - bounced: the address does not exist;
 * - needs_review: nobody could tell yet (a person reads it).
 */
export const REPLY_CATEGORY_MEANINGS = [
	'interested',
	'meeting',
	'question',
	'referral',
	'later',
	'not_interested',
	'out_of_office',
	'unsubscribe',
	'bounced',
	'needs_review',
] as const;
export type ReplyCategoryMeaning = (typeof REPLY_CATEGORY_MEANINGS)[number];

export const REPLY_CATEGORY_MEANING_LABELS: Readonly<Record<ReplyCategoryMeaning, string>> = {
	interested: 'Interested',
	meeting: 'Wants a Meeting',
	question: 'Question',
	referral: 'Someone Else',
	later: 'Not Now',
	not_interested: 'Not Interested',
	out_of_office: 'Out of Office',
	unsubscribe: 'Unsubscribe',
	bounced: 'Bounced',
	needs_review: 'Needs a Look',
};

/** Seeded per tenant (title case, Chris 10/26/26); admins rename, reorder or add. */
export const DEFAULT_REPLY_CATEGORIES: ReadonlyArray<DefaultStatusValueModel> = [
	{ name: 'Needs a Look', outcome: 'needs_review' },
	{ name: 'Interested', outcome: 'interested' },
	{ name: 'Wants a Meeting', outcome: 'meeting' },
	{ name: 'Question', outcome: 'question' },
	{ name: 'Someone Else', outcome: 'referral' },
	{ name: 'Not Now', outcome: 'later' },
	{ name: 'Not Interested', outcome: 'not_interested' },
	{ name: 'Out of Office', outcome: 'out_of_office' },
	{ name: 'Unsubscribe', outcome: 'unsubscribe' },
	{ name: 'Bounced', outcome: 'bounced' },
];

/**
 * The meaning of a call outcome stored on a call. `call.dispositionCode`
 * holds the catalog value's NAME at the time of the call (PD-14436), so the
 * name is matched against the tenant's values ignoring case and surrounding
 * spaces. Null for a typed "Other…" outcome or a value since renamed or
 * removed: the caller decides what an unknown outcome does.
 */
export function findCallOutcomeMeaning(
	dispositionName: string | null | undefined,
	values: ReadonlyArray<{ name: string; outcome?: string | null }>,
): CallOutcomeMeaning | null {
	const key = dispositionName?.trim().toLowerCase() ?? '';

	if (key === '') {
		return null;
	}

	const match = values.find((value) => value.name.trim().toLowerCase() === key);
	const outcome = match?.outcome ?? null;

	return outcome != null && (CALL_OUTCOME_MEANINGS as ReadonlyArray<string>).includes(outcome)
		? (outcome as CallOutcomeMeaning)
		: null;
}
